import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "input"]
  static values = {
    userId: Number,
    projectId: Number,
    initialValue: Number,
    userCapacity: { type: Number, default: 76 }, // Default fortnight capacity
    editable: { type: Boolean, default: true }
  }

  connect() {
    this.originalValue = this.initialValueValue
    this.currentMode = localStorage.getItem('matrix_view_mode') || 'hours'

    // Listen for mode changes
    window.addEventListener('matrix:mode-changed', this.handleModeChange.bind(this))

    // Update display based on current mode
    this.updateDisplayForMode()
  }

  disconnect() {
    window.removeEventListener('matrix:mode-changed', this.handleModeChange.bind(this))
  }

  handleModeChange(event) {
    this.currentMode = event.detail.mode
    this.updateDisplayForMode()
  }

  updateDisplayForMode() {
    if (!this.hasDisplayTarget) return

    const hours = this.originalValue
    if (this.currentMode === 'days') {
      const days = this.hoursToDays(hours)
      const displayValue = days === 0 ? '-' : days.toFixed(1)
      // Update only the numeric part, preserve actual hours if they exist
      const currentText = this.displayTarget.innerHTML
      if (currentText.includes('/')) {
        // Has actual hours, keep the structure but update the planned value
        const parts = currentText.match(/<span[^>]*>([^<]+)<\/span>/)
        if (parts) {
          this.displayTarget.innerHTML = currentText.replace(
            /<span class="font-semibold">([^<]+)<\/span>/,
            `<span class="font-semibold">${displayValue}</span>`
          )
        }
      } else {
        this.displayTarget.textContent = displayValue
      }
    } else {
      // Hours mode - show original value
      const displayValue = hours === 0 ? '-' : hours
      const currentText = this.displayTarget.innerHTML
      if (currentText.includes('/')) {
        this.displayTarget.innerHTML = currentText.replace(
          /<span class="font-semibold">([^<]+)<\/span>/,
          `<span class="font-semibold">${displayValue}</span>`
        )
      } else {
        this.displayTarget.textContent = displayValue
      }
    }
  }

  hoursToDays(hours) {
    const dailyCapacity = this.userCapacityValue / 10 // 10 working days in a fortnight
    return hours / dailyCapacity
  }

  daysToHours(days) {
    const dailyCapacity = this.userCapacityValue / 10
    return Math.round(days * dailyCapacity * 10) / 10 // Round to 1 decimal
  }

  edit(event) {
    event.preventDefault()
    if (!this.editableValue) {
      return // Don't allow editing if not editable
    }
    this.showInput()
  }

  showInput() {
    // Set input value based on current mode
    if (this.currentMode === 'days') {
      this.inputTarget.value = this.hoursToDays(this.originalValue).toFixed(1)
      this.inputTarget.step = '0.1'
      this.inputTarget.placeholder = 'Days'
    } else {
      this.inputTarget.value = this.originalValue
      this.inputTarget.step = '1'
      this.inputTarget.placeholder = 'Hours'
    }

    this.displayTarget.classList.add('hidden')
    this.inputTarget.classList.remove('hidden')
    this.inputTarget.focus()
    this.inputTarget.select()
  }

  hideInput() {
    this.inputTarget.classList.add('hidden')
    this.displayTarget.classList.remove('hidden')
  }

  cancel(event) {
    event.preventDefault()
    this.inputTarget.value = this.originalValue
    this.hideInput()
  }

  async save(event) {
    // Don't submit if Tab was pressed (let it move focus naturally)
    if (event.type === 'keydown' && event.key === 'Tab') {
      event.preventDefault()
    }

    // Parse input value and convert to hours if in days mode
    let inputValue = parseFloat(this.inputTarget.value) || 0
    let newValue

    if (this.currentMode === 'days') {
      // Convert days to hours
      newValue = Math.round(this.daysToHours(inputValue))
    } else {
      newValue = Math.round(inputValue)
    }

    // No change, just close
    if (newValue === this.originalValue) {
      this.hideInput()
      return
    }

    // Show saving state
    this.element.classList.add('opacity-50')

    try {
      const response = await fetch('/assigned_works/update_cell', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          user_id: this.userIdValue,
          project_id: this.projectIdValue,
          hours: newValue
        })
      })

      const data = await response.json()

      if (data.success) {
        this.originalValue = newValue
        this.displayTarget.textContent = newValue === 0 ? '-' : newValue
        this.hideInput()
        this.showSuccess()

        // Dispatch event to update totals
        window.dispatchEvent(new CustomEvent('matrix:recalculate', {
          detail: data
        }))
      } else {
        this.showError(data.errors ? data.errors.join(', ') : 'Failed to save')
      }
    } catch (error) {
      this.showError('Network error. Please try again.')
      console.error(error)
    } finally {
      this.element.classList.remove('opacity-50')
    }
  }

  updateDisplay(event) {
    // Update this cell's display if the recalculation affects it
    const data = event.detail
    if (data.assignment &&
        data.assignment.user_id === this.userIdValue &&
        data.assignment.project_id === this.projectIdValue) {
      this.originalValue = data.assignment.hours
      this.displayTarget.textContent = data.assignment.hours === 0 ? '-' : data.assignment.hours
    }
  }

  showSuccess() {
    this.element.classList.add('ring-2', 'ring-green-500')
    setTimeout(() => {
      this.element.classList.remove('ring-2', 'ring-green-500')
    }, 1000)
  }

  showError(message) {
    this.element.classList.add('ring-2', 'ring-red-500')

    // Show toast notification
    window.dispatchEvent(new CustomEvent('toast:show', {
      detail: { message, type: 'error' }
    }))

    setTimeout(() => {
      this.element.classList.remove('ring-2', 'ring-red-500')
    }, 2000)
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').content
  }
}
